import type { ReactNode } from 'react';
import { useState } from 'react';
import GbFlag from '@assets/icons/flags/gb.svg';
import JpFlag from '@assets/icons/flags/jp.svg';
import NoFlag from '@assets/icons/flags/no.svg';
import PlFlag from '@assets/icons/flags/pl.svg';
import { BaseSelect } from '@components/select';
import { TEST_IDS } from '@constants/testIds.ts';
import { useLingui } from '@lingui/react/macro';
import type { AppLocale } from '@utils/i18n.ts';
import { activateLocale, persistLocale, SUPPORTED_LOCALES } from '@utils/i18n.ts';

import { FlagLabel, LocaleSwitcherSelectWrapper, LocaleSwitcherWrapper } from './styled.tsx';

const LOCALES: Array<{ value: AppLocale; name: string; flag: ReactNode }> = [
    { value: 'en', name: 'English', flag: <GbFlag /> },
    { value: 'pl', name: 'Polski', flag: <PlFlag /> },
    { value: 'no', name: 'Norsk', flag: <NoFlag /> },
    { value: 'ja', name: '日本語', flag: <JpFlag /> },
];
const LOCALE_OPTIONS: Array<{ value: AppLocale; title: string; label: ReactNode }> = LOCALES.map(
    ({ value, name, flag }) => ({
        value,
        title: name,
        label: (
            <FlagLabel role="img" aria-label={name}>
                {flag}
            </FlagLabel>
        ),
    }),
);
const isAppLocale = (value: string): value is AppLocale => (SUPPORTED_LOCALES as readonly string[]).includes(value);

export const LocaleSwitcher = () => {
    const { i18n } = useLingui();
    const [isLoading, setIsLoading] = useState(false);
    const currentLocale = isAppLocale(i18n.locale) ? i18n.locale : 'en';

    const handleChange = async (locale: AppLocale) => {
        setIsLoading(true);
        try {
            await activateLocale(locale);
            persistLocale(locale);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <LocaleSwitcherWrapper>
            <LocaleSwitcherSelectWrapper>
                <BaseSelect
                    id="locale-switcher"
                    value={currentLocale}
                    loading={isLoading}
                    disabled={isLoading}
                    onChange={handleChange}
                    options={LOCALE_OPTIONS}
                    data-cy={TEST_IDS.SHARED.LOCALE_SWITCHER}
                    variant="borderless"
                    suffixIcon={null}
                />
            </LocaleSwitcherSelectWrapper>
        </LocaleSwitcherWrapper>
    );
};
